import * as fc from 'fast-check';
import _ from 'underscore';

import {ARCHIVE_RECORD_GQL} from 'archive/__tests__/ArchiveRecord-fixtures';
import {restArrayToGraphQL, restToGraphQL} from 'graphql-client/declarative-translations/external/translate';
import {ContainerSchema, StorableSchemaSummary} from 'samples/storables/StorableSchemaConstants';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {REGISTRY} from 'test/fixtures/registry/RegistryFixtures';
import deepFreezeObject from 'test/lib/deepFreezeObject';

import {ContainerSchemaGraphQL} from '../../../../graphql-fragments/inventory/schemas/__generated/ContainerSchemaGraphQL';

export const WELL_CONTAINER_SCHEMA = deepFreezeObject<ContainerSchema>({
  prefix: 'w',
  registryId: REGISTRY.id,
  name: 'Well',
  allFields: [],
  fields: [],
  id: 'consch_2v2WzY5S',
  iconId: 'vial',
  archiveRecord: null,
  sqlIdentifier: 'well',
});

export const WELL_CONTAINER_SCHEMA_GQL = deepFreezeObject<ContainerSchemaGraphQL>(
  restToGraphQL('ContainerSchema', WELL_CONTAINER_SCHEMA)
);

export const VIAL_FIELD_1 = deepFreezeObject<SchemaField>({
  archiveRecord: null,
  fieldType: FieldType.TEXT,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  id: 11,
  dbId: 11,
  apiId: 'tsf_123',
  schemaFieldSelectorId: null,
  name: 'vial assay',
  systemName: 'vial_assay',
  displayName: 'vial assay',
});

export const VIAL_FIELD_2 = deepFreezeObject<SchemaField>({
  archiveRecord: null,
  fieldType: FieldType.TEXT,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  id: 12,
  dbId: 12,
  apiId: 'tsf_234',
  schemaFieldSelectorId: null,
  name: 'vial volume',
  systemName: 'vial_volume',
  displayName: 'vial volume',
});

export const VIAL_FIELD_3 = deepFreezeObject<SchemaField>({
  archiveRecord: null,
  fieldType: FieldType.SELECTOR,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  id: 25,
  dbId: 25,
  apiId: 'tsf_345',
  requiredLink: null,
  schemaFieldSelectorId: 'sfs_r1dU5DTZ',
  name: 'Dropdown',
  systemName: 'dropdown',
  displayName: 'Dropdown',
});

export const VIAL_CONTAINER_SCHEMA = deepFreezeObject<ContainerSchema>({
  prefix: 'V',
  registryId: REGISTRY.id,
  name: 'Vial',
  allFields: [VIAL_FIELD_1, VIAL_FIELD_2, VIAL_FIELD_3],
  fields: [VIAL_FIELD_1, VIAL_FIELD_2, VIAL_FIELD_3],
  id: 'consch_E82zj1xB',
  iconId: 'vial',
  archiveRecord: null,
  sqlIdentifier: 'vial',
});

export const VIAL_CONTAINER_SCHEMA_GQL = deepFreezeObject<ContainerSchemaGraphQL>(
  restToGraphQL('ContainerSchema', VIAL_CONTAINER_SCHEMA)
);

export const TUBE_FIELD_1 = deepFreezeObject<SchemaField>({
  archiveRecord: null,
  fieldType: FieldType.TEXT,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  id: 13,
  dbId: 13,
  apiId: 'tsf_12a',
  schemaFieldSelectorId: null,
  name: 'tube mass',
  systemName: 'tube_mass',
  displayName: 'tube mass',
});

const TUBE_FIELD_2 = deepFreezeObject<SchemaField>({
  archiveRecord: null,
  fieldType: FieldType.TEXT,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  requiredLink: null,
  id: 14,
  dbId: 14,
  apiId: 'tsf_12b',
  schemaFieldSelectorId: null,
  name: 'tube intensity',
  systemName: 'tube_intensity',
  displayName: 'tube intensity',
});

export const TUBE_CONTAINER_SCHEMA_SUMMARY = deepFreezeObject<StorableSchemaSummary>({
  archiveRecord: null,
  iconId: 'vial',
  id: 'consch_MQ6ZYOql',
  name: 'Tube',
  prefix: 'T',
  registryId: REGISTRY.id,
  sqlIdentifier: 'tube',
});

export const TUBE_CONTAINER_SCHEMA = deepFreezeObject<ContainerSchema>({
  ...TUBE_CONTAINER_SCHEMA_SUMMARY,
  allFields: [TUBE_FIELD_1, TUBE_FIELD_2],
  fields: [TUBE_FIELD_1, TUBE_FIELD_2],
});

export const TUBE_CONTAINER_SCHEMA_GQL = deepFreezeObject<ContainerSchemaGraphQL>(
  restToGraphQL('ContainerSchema', TUBE_CONTAINER_SCHEMA)
);

export const ARCHIVED_CONTAINER_SCHEMA = deepFreezeObject<ContainerSchema>({
  ...VIAL_CONTAINER_SCHEMA,
  archiveRecord: {purpose: 'Made in error'},
});

export const ARCHIVED_CONTAINER_SCHEMA_GQL = deepFreezeObject<ContainerSchemaGraphQL>({
  ...VIAL_CONTAINER_SCHEMA_GQL,
  archiveRecord: ARCHIVE_RECORD_GQL,
});

export const CONTAINER_SCHEMAS = deepFreezeObject<Array<ContainerSchema>>(
  _.sortBy([TUBE_CONTAINER_SCHEMA, VIAL_CONTAINER_SCHEMA, WELL_CONTAINER_SCHEMA], 'name')
);

export const CONTAINER_SCHEMAS_GQL = deepFreezeObject<Array<ContainerSchemaGraphQL>>(
  restArrayToGraphQL('ContainerSchema', CONTAINER_SCHEMAS)
);

export const ContainerSchemaArbitrary: fc.Arbitrary<ContainerSchema> = fc.oneof(
  fc.constant(WELL_CONTAINER_SCHEMA),
  fc.constant(VIAL_CONTAINER_SCHEMA),
  fc.constant(TUBE_CONTAINER_SCHEMA)
);
